import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Ask before throwing away unsaved edits: in-app navigation (another tab, the
 * breadcrumb, the sidebar) gets a confirm dialog, and closing or reloading the
 * browser tab gets the browser's own prompt. Render the returned `dialog`.
 */
export function useLeaveGuard(dirty: boolean) {
  const { t } = useTranslation("build");
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    globalThis.addEventListener("beforeunload", onBeforeUnload);
    return () => globalThis.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  return (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      title={t("leaveGuard.title")}
      body={t("leaveGuard.body")}
      confirmLabel={t("leaveGuard.leave")}
      onConfirm={() => blocker.proceed?.()}
      onClose={() => blocker.reset?.()}
    />
  );
}
